import React, { useEffect, useState } from 'react';
import { IndianRupee, TrendingUp, TrendingDown, Loader2, Building2, Wallet as WalletIcon, AlertCircle, Receipt, PieChart, CalendarClock, HandCoins, ShieldCheck, Plus, X, Trash2, Play } from 'lucide-react';import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

const HQ_CATEGORIES = [
  { id: 'franchise_royalty',   label: 'Franchise Royalty' },
  { id: 'company_commission',  label: 'Company Commission' },
  { id: 'software_fee',        label: 'Software Fee' },
  { id: 'marketing_fee',       label: 'Marketing Fee' },
  { id: 'advertisement',       label: 'Advertisement Charges' },
  { id: 'franchise_deposit',   label: 'Franchise Deposit' },
  { id: 'security_deposit',    label: 'Security Deposit' },
  { id: 'renewal_fee',         label: 'Franchise Renewal Fee' },
];

const AdminErp = () => {
  const [data, setData] = useState(null);
  const [franchises, setFranchises] = useState([]);
  const [charges, setCharges] = useState([]);
  const [cashflow, setCashflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddCharge, setShowAddCharge] = useState(false);
  const [chargeForm, setChargeForm] = useState({ franchise_id: '', category: 'franchise_royalty', amount: '', frequency: 'monthly', active: true, note: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, fr, ch, cf] = await Promise.all([
        axios.get(`${API}/admin/erp/dashboard`, authCfg()),
        axios.get(`${API}/franchises`, authCfg()),
        axios.get(`${API}/admin/erp/hq-charges`, authCfg()),
        axios.get(`${API}/admin/erp/cashflow`, authCfg()),
      ]);
      setData(d.data);
      setFranchises(fr.data || []);
      setCharges(ch.data.charges || []);
      setCashflow(cf.data);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to load ERP dashboard' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addCharge = async () => {
    if (!chargeForm.franchise_id || !chargeForm.amount) { setMsg({ ok: false, text: 'Franchise + amount required' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/erp/hq-charges`, { ...chargeForm, amount: Number(chargeForm.amount) }, authCfg());
      setMsg({ ok: true, text: 'HQ charge added' });
      setShowAddCharge(false);
      setChargeForm({ franchise_id: '', category: 'franchise_royalty', amount: '', frequency: 'monthly', active: true, note: '' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to add' });
    } finally { setBusy(false); }
  };

  const deleteCharge = async (cid) => {
    if (!window.confirm('Delete this HQ charge? Past posted entries remain in the ledger.')) return;
    try { await axios.delete(`${API}/admin/erp/hq-charges/${cid}`, authCfg()); load(); } catch {}
  };

  const runMonthly = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/erp/hq-charges/run-monthly`, {}, authCfg());
      setMsg({ ok: true, text: `Posted ${r.posted} HQ charge(s) for ${r.month}` });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Run failed' });
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout title="Master ERP Dashboard">
      {loading || !data ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
      ) : (
        <>
          {msg && (
            <div data-testid="erp-msg" className={`mb-4 text-sm rounded-lg px-3 py-2 ${msg.ok ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>
          )}

          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Total Revenue"        value={fmt(data.summary.total_revenue)}          icon={IndianRupee}  color="#2E7D32" bg="#E8F5E9" testid="erp-total-revenue" />
            <Kpi label="Today's Bookings"     value={fmt(data.summary.today_amount)}           icon={CalendarClock} color="#EA580C" bg="#FFF3E0" testid="erp-today-amount" />
            <Kpi label="Company Profit"       value={fmt(data.summary.company_profit)}         icon={TrendingUp}    color="#7B1FA2" bg="#F3E5F5" testid="erp-company-profit" />
            <Kpi label="Franchise Earnings"   value={fmt(data.summary.franchise_earnings_total)} icon={Building2}   color="#1E88E5" bg="#DBEAFE" testid="erp-franchise-earnings" />
            <Kpi label="GST Payable"          value={fmt(data.summary.gst_payable)}            icon={Receipt}       color="#D97706" bg="#FEF3C7" testid="erp-gst-payable" />
            <Kpi label="Cleaner Payout"       value={fmt(data.summary.cleaner_payout_total)}   icon={HandCoins}     color="#0891B2" bg="#CFFAFE" testid="erp-cleaner-payout" />
            <Kpi label="Pending Collections"  value={`${fmt(data.summary.pending_collections)} · ${data.summary.pending_bookings_count} booking${data.summary.pending_bookings_count === 1 ? '' : 's'}`} icon={AlertCircle} color="#DC2626" bg="#FEE2E2" testid="erp-pending" />
            <Kpi label="Wallet Balance"       value={fmt(data.summary.wallet_balance_across_users)} icon={WalletIcon} color="#059669" bg="#D1FAE5" testid="erp-wallet-balance" />
            <Kpi label="Franchise Expenses"   value={fmt(data.summary.total_franchise_expenses)} icon={TrendingDown} color="#7C2D12" bg="#FEF3C7" testid="erp-total-expenses" />
            <Kpi label="Cash Flow"            value={fmt(data.summary.cash_flow)}              icon={PieChart}      color="#0E7490" bg="#CFFAFE" testid="erp-cash-flow" />
          </div>

          {/* District-wise + Franchise-wise income */}
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <Card title="District-wise income" testid="erp-district-income">
              <Table
                rows={data.district_income || []}
                cols={[
                  { label: 'District', key: 'district' },
                  { label: 'Company Rev', key: 'company_revenue', money: true },
                  { label: 'Franchise Rev', key: 'franchise_earnings', money: true },
                ]}
              />
            </Card>

            <Card title="Monthly comparison (last 6 mo)" testid="erp-monthly">
              <Table
                rows={data.monthly_comparison || []}
                cols={[
                  { label: 'Month', key: 'month' },
                  { label: 'Revenue', key: 'amount', money: true },
                ]}
              />
            </Card>
          </div>

          {/* Cash flow chart */}
          {cashflow && (
            <div className="mt-6 bg-white border border-neutral-200 rounded-2xl" data-testid="erp-cashflow-chart">
              <div className="px-4 py-3 border-b border-neutral-100 flex items-center gap-3">
                <p className="font-bold text-sm flex-1">Cash flow — last 30 days (all districts)</p>
                <span className="text-xs text-[#2E7D32] font-bold">+₹{cashflow.totals.inflow.toLocaleString('en-IN')}</span>
                <span className="text-xs text-red-600 font-bold">−₹{cashflow.totals.outflow.toLocaleString('en-IN')}</span>
                <span className={`text-xs font-extrabold ${cashflow.totals.net >= 0 ? 'text-[#2E7D32]' : 'text-red-600'}`}>Net ₹{cashflow.totals.net.toLocaleString('en-IN')}</span>
              </div>
              <MiniCashFlow buckets={cashflow.buckets || []} />
              {(cashflow.by_district || []).length > 0 && (
                <div className="border-t border-neutral-100 px-4 py-3">
                  <p className="text-xs font-bold text-neutral-600 mb-2">District-wise cash movement (window)</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left">
                        <tr><th className="px-2 py-1 text-[10px] uppercase tracking-wider text-neutral-500 font-bold">District</th><th className="px-2 py-1 text-right text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Inflow</th><th className="px-2 py-1 text-right text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Outflow</th><th className="px-2 py-1 text-right text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Net</th></tr>
                      </thead>
                      <tbody>
                        {cashflow.by_district.map((d) => (
                          <tr key={d.district} className="border-t border-neutral-100">
                            <td className="px-2 py-1.5 text-xs">{d.district}</td>
                            <td className="px-2 py-1.5 text-right text-xs text-[#2E7D32]">+₹{d.inflow.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-1.5 text-right text-xs text-red-600">−₹{d.outflow.toLocaleString('en-IN')}</td>
                            <td className={`px-2 py-1.5 text-right text-xs font-bold ${d.net >= 0 ? 'text-[#2E7D32]' : 'text-red-600'}`}>₹{d.net.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Period Locks (iter-15a) */}
          <div className="mt-6 bg-white border border-neutral-200 rounded-2xl" data-testid="erp-period-locks">
            <div className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-center gap-2">
              <p className="font-bold text-sm flex-1">Accounting period locks</p>
              <button
                data-testid="lock-month-btn"
                onClick={async () => {
                  const period = window.prompt('Lock which month? (YYYY-MM)', new Date().toISOString().slice(0, 7));
                  if (!period) return;
                  const reason = window.prompt('Reason for lock (optional)', 'Month close') || '';
                  try { await axios.post(`${API}/admin/period-locks`, { kind: 'month', period, reason }, authCfg()); setMsg({ ok: true, text: `Locked ${period}` }); load(); }
                  catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' }); }
                }}
                className="inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                Lock month
              </button>
              <button
                data-testid="lock-year-btn"
                onClick={async () => {
                  const period = window.prompt('Lock which financial year? (YYYY)', String(new Date().getFullYear()));
                  if (!period) return;
                  const reason = window.prompt('Reason for lock (optional)', 'Year-end close') || '';
                  try { await axios.post(`${API}/admin/period-locks`, { kind: 'year', period, reason }, authCfg()); setMsg({ ok: true, text: `Locked FY ${period}` }); load(); }
                  catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' }); }
                }}
                className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 hover:border-[#2E7D32] text-neutral-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
                Lock year
              </button>
            </div>
            <PeriodLocksTable />
          </div>

          {/* HQ recurring charges configurator */}
          <div className="mt-6 bg-white border border-neutral-200 rounded-2xl">
            <div className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-center gap-2">
              <p className="font-bold text-sm flex-1">HQ recurring charges (Royalty, Software fee, Marketing fee, Renewal…)</p>
              <button data-testid="erp-add-charge-btn" onClick={() => setShowAddCharge(true)} className="inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                <Plus className="w-3.5 h-3.5" /> Add charge
              </button>
              <button data-testid="erp-run-monthly-btn" onClick={runMonthly} disabled={busy} className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 hover:border-[#2E7D32] text-neutral-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
                <Play className="w-3.5 h-3.5" /> {busy ? 'Running…' : 'Post this month'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                  <tr><Th>Franchise</Th><Th>Category</Th><Th>Amount</Th><Th>Frequency</Th><Th>Status</Th><Th className="text-right">Action</Th></tr>
                </thead>
                <tbody>
                  {charges.map((c) => (
                    <tr key={c.id} data-testid={`hq-charge-${c.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-xs">{c.district || c.franchise_id}</p>
                        <p className="text-[10px] text-neutral-400 font-mono">{c.franchise_id}</p>
                      </td>
                      <td className="px-4 py-3 text-xs">{HQ_CATEGORIES.find(x => x.id === c.category)?.label || c.category}</td>
                      <td className="px-4 py-3 text-xs font-bold">₹{c.amount.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-xs">{c.frequency}</td>
                      <td className="px-4 py-3 text-xs">{c.active ? <span className="text-[#2E7D32] font-bold">Active</span> : <span className="text-neutral-400">Inactive</span>}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => deleteCharge(c.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {charges.length === 0 && <tr><td colSpan="6" className="text-center text-neutral-400 py-8">No HQ recurring charges configured. Add one to start billing franchises monthly.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-4 text-[11px] text-neutral-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2E7D32]" /> Every paid booking auto-posts a 4-way split: Company revenue, Franchise earnings, Cleaner payable, GST payable. Cancellations reverse automatically.</p>
        </>
      )}

      {showAddCharge && (
        <Modal onClose={() => setShowAddCharge(false)} title="Add HQ recurring charge">
          <label className="block text-xs font-semibold text-neutral-600">Franchise</label>
          <select data-testid="hq-charge-franchise" value={chargeForm.franchise_id} onChange={(e) => setChargeForm({ ...chargeForm, franchise_id: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="">Select franchise</option>
            {franchises.map((f) => <option key={f.franchise_id} value={f.franchise_id}>{f.district} ({f.franchise_id})</option>)}
          </select>
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Category</label>
          <select data-testid="hq-charge-category" value={chargeForm.category} onChange={(e) => setChargeForm({ ...chargeForm, category: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            {HQ_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Amount (₹)</label>
          <input data-testid="hq-charge-amount" type="text" inputMode="numeric" value={chargeForm.amount} onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value.replace(/[^0-9]/g, '') })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="5000" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Frequency</label>
          <select data-testid="hq-charge-frequency" value={chargeForm.frequency} onChange={(e) => setChargeForm({ ...chargeForm, frequency: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="monthly">Monthly (auto-post 1st)</option>
            <option value="yearly">Yearly</option>
            <option value="one_time">One-time</option>
          </select>
          <button data-testid="hq-charge-submit" onClick={addCharge} disabled={busy} className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Saving…' : 'Add HQ charge'}
          </button>
        </Modal>
      )}
    </AdminLayout>
  );
};

const Kpi = ({ label, value, icon: Icon, color, bg, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-xl p-4">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: bg }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="mt-3 text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</p>
    <p className="font-extrabold text-lg mt-0.5">{value}</p>
  </div>
);
const Th = ({ children, className = '' }) => <th className={`px-4 py-2.5 text-xs font-bold text-neutral-500 uppercase tracking-wider ${className}`}>{children}</th>;
const Card = ({ title, children, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
    <div className="px-4 py-3 border-b border-neutral-100"><p className="font-bold text-sm">{title}</p></div>
    <div className="overflow-x-auto">{children}</div>
  </div>
);
const Table = ({ rows, cols }) => (
  <table className="w-full text-sm">
    <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
      <tr>{cols.map((c) => <Th key={c.key}>{c.label}</Th>)}</tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i} className="border-b border-neutral-100">
          {cols.map((c) => (
            <td key={c.key} className={`px-4 py-2.5 text-xs ${c.money ? 'font-bold text-[#2E7D32]' : ''}`}>
              {c.money ? `₹${(r[c.key] || 0).toLocaleString('en-IN')}` : (r[c.key] || '—')}
            </td>
          ))}
        </tr>
      ))}
      {rows.length === 0 && <tr><td colSpan={cols.length} className="text-center text-neutral-400 py-6 text-xs">No data yet</td></tr>}
    </tbody>
  </table>
);
const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="font-extrabold">{title}</p>
        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const PeriodLocksTable = () => {
  const [locks, setLocks] = React.useState([]);
  React.useEffect(() => {
    axios.get(`${API}/admin/period-locks`, authCfg()).then(({ data }) => setLocks(data.locks || [])).catch(() => {});
  }, []);
  if (!locks.length) return <p className="px-4 py-6 text-center text-xs text-neutral-400">No periods locked yet.</p>;
  const unlock = async (id) => {
    if (!window.confirm('Unlock this period? Future postings inside will be allowed.')) return;
    try { await axios.delete(`${API}/admin/period-locks/${id}`, authCfg()); setLocks(locks.filter(l => l.id !== id)); } catch {}
  };
  return (
    <table className="w-full text-sm">
      <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
        <tr>
          <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase">Kind</th>
          <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase">Period</th>
          <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase">Scope</th>
          <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase">Reason</th>
          <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase text-right">Action</th>
        </tr>
      </thead>
      <tbody>
        {locks.map((l) => (
          <tr key={l.id} data-testid={`lock-row-${l.id}`} className="border-b border-neutral-100">
            <td className="px-4 py-2 text-xs uppercase font-bold">{l.kind}</td>
            <td className="px-4 py-2 text-xs font-mono">{l.period}</td>
            <td className="px-4 py-2 text-xs text-neutral-600">{l.franchise_id || 'All franchises'}</td>
            <td className="px-4 py-2 text-xs text-neutral-600">{l.reason || '—'}</td>
            <td className="px-4 py-2 text-right">
              <button data-testid={`unlock-btn-${l.id}`} onClick={() => unlock(l.id)} className="text-xs bg-white border border-neutral-200 hover:border-red-500 hover:text-red-600 font-semibold px-2 py-1 rounded-lg">Unlock</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const MiniCashFlow = ({ buckets }) => {  const max = Math.max(1, ...buckets.map(b => Math.max(b.inflow || 0, b.outflow || 0)));
  if (!buckets.length) return <p className="px-4 py-8 text-center text-neutral-400 text-xs">No cash flow activity in the window.</p>;
  return (
    <div className="px-4 py-4">
      <div className="flex items-end gap-1 h-32 border-b border-neutral-200">
        {buckets.map((b) => (
          <div key={b.date} className="flex-1 flex items-end gap-[1px] h-full" title={`${b.date}: +₹${b.inflow.toLocaleString('en-IN')} / −₹${b.outflow.toLocaleString('en-IN')}`}>
            <div className="flex-1 bg-[#2E7D32] rounded-t" style={{ height: `${Math.max(2, (b.inflow / max) * 100)}%` }} />
            <div className="flex-1 bg-red-500 rounded-t" style={{ height: `${Math.max(2, (b.outflow / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-neutral-500">
        <span>{buckets[0]?.date}</span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#2E7D32] rounded-sm"></span> Inflow</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm"></span> Outflow</span>
        </div>
        <span>{buckets[buckets.length - 1]?.date}</span>
      </div>
    </div>
  );
};

export default AdminErp;
